import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  School as CoachIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as ClubIcon,
  Person as PersonIcon,
  IceSkating as SkaterIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  LinkOff as LinkOffIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { REGISTRATION_BASE_URL, getApiBaseUrlWithFallback } from '../config/environment';

/** Capitalize first letter of each word for display (e.g. "stephanie rowland" → "Stephanie Rowland"). */
function titleCaseName(str) {
  if (!str || typeof str !== 'string') return str;
  return str
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default function InterclubCoaches({ interclubId, interclub }) {
  const { getIdToken } = useFirebaseAuth();
  const { getHeaders: getImpersonationHeaders } = useImpersonation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeMyOnly = searchParams.get('scope') === 'my';

  const [coaches, setCoaches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [stats, setStats] = useState({ total: 0, withSkaters: 0, totalSkaters: 0, compliant: 0, nonCompliant: 0, expiringSoon: 0, unlinked: 0 });
  const [selectedCompliance, setSelectedCompliance] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const loadClubs = useCallback(async () => {
    if (!interclubId) return;
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const seriesParam = scopeMyOnly ? '' : '?series=true';
      const headers = {};
      if (seriesParam) {
        const token = await getIdToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}/clubs${seriesParam}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setClubs(data || []);
      }
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  }, [interclubId, scopeMyOnly, getIdToken]);

  const loadCoaches = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const seriesParam = scopeMyOnly ? '' : '?series=true';
      const response = await fetch(`${REGISTRATION_BASE_URL}/interclub/${interclubId}/coaches${seriesParam}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch coaches');
      }
      
      const data = await response.json();
      setCoaches(data.coaches || []);
      setStats(data.stats || { total: 0, withSkaters: 0, totalSkaters: 0 });
    } catch (err) {
      console.error('Error loading coaches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, getIdToken, getImpersonationHeaders, scopeMyOnly]);

  useEffect(() => {
    loadClubs();
    loadCoaches();
  }, [loadClubs, loadCoaches]);

  const handleCoachClick = (coach) => {
    setSelectedCoach(coach);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedCoach(null);
    setScanError(null);
  };

  const handleScanForSkaters = useCallback(async () => {
    if (!selectedCoach || !interclubId) return;
    try {
      setScanLoading(true);
      setScanError(null);
      const token = await getIdToken();
      if (!token) throw new Error('Unable to get authentication token');
      const response = await fetch(
        `${REGISTRATION_BASE_URL}/interclub/${interclubId}/coaches/${selectedCoach.id}/skaters`,
        { headers: { Authorization: `Bearer ${token}`, ...getImpersonationHeaders() } }
      );
      if (!response.ok) throw new Error('Scan failed');
      const data = await response.json();
      const skaters = data.skaters || [];
      const count = skaters.length;
      setSelectedCoach((prev) => (prev ? { ...prev, skaters, skaterCount: count } : null));
      setCoaches((prev) =>
        prev.map((c) => (c.id === selectedCoach.id ? { ...c, skaters, skaterCount: count } : c))
      );
    } catch (err) {
      console.error('Scan for skaters failed:', err);
      setScanError(err.message || 'Scan failed');
    } finally {
      setScanLoading(false);
    }
  }, [selectedCoach, interclubId, getIdToken, getImpersonationHeaders]);

  const filteredCoaches = coaches.filter(coach => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (coach.displayName || '').toLowerCase().includes(searchLower) ||
      (coach.email || '').toLowerCase().includes(searchLower) ||
      (coach.clubName || '').toLowerCase().includes(searchLower)
    );
    
    if (!matchesSearch) return false;
    
    if (selectedClub && coach.clubId !== selectedClub) return false;
    
    // Compliance filter
    if (selectedCompliance) {
      if (selectedCompliance === 'unlinked' && coach.isUsfsLinked) return false;
      if (selectedCompliance === 'Compliant' && coach.complianceStatus !== 'Compliant') return false;
      if (selectedCompliance === 'Non-Compliant' && coach.complianceStatus !== 'Non-Compliant') return false;
      if (selectedCompliance === 'Expiring Soon' && coach.complianceStatus !== 'Expiring Soon') return false;
    }
    
    return true;
  });

  const sortedCoaches = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredCoaches].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.displayName || '').toLowerCase();
          bVal = (b.displayName || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'club':
          aVal = (a.clubName || '').toLowerCase();
          bVal = (b.clubName || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'compliance':
          aVal = (a.complianceStatus || 'zzz').toLowerCase();
          bVal = (b.complianceStatus || 'zzz').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'skaters':
          aVal = a.skaterCount ?? 0;
          bVal = b.skaterCount ?? 0;
          return dir * (aVal - bVal);
        case 'interclub':
          aVal = (a.interclubName || '').toLowerCase();
          bVal = (b.interclubName || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        default:
          return 0;
      }
    });
  }, [filteredCoaches, sortField, sortDirection]);

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection((prev) => (prev === 'asc' && sortField === field ? 'desc' : 'asc'));
  };

  const showInterclubColumn = useMemo(() => coaches.some((c) => c.interclubName), [coaches]);

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
            }}>
              <CoachIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Coaches
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {scopeMyOnly ? 'Approved coaches in your interclub only' : 'Approved coaches across all interclubs in the series'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              value={scopeMyOnly ? 'my' : 'all'}
              exclusive
              onChange={(_, value) => {
                if (value == null) return;
                const next = new URLSearchParams(searchParams);
                if (value === 'my') next.set('scope', 'my');
                else next.delete('scope');
                setSearchParams(next);
              }}
              size="small"
            >
              <ToggleButton value="all">All interclubs</ToggleButton>
              <ToggleButton value="my">{interclub?.name ? `${interclub.name} only` : 'My interclub only'}</ToggleButton>
            </ToggleButtonGroup>
            <IconButton 
              onClick={loadCoaches} 
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total Coaches: ${stats.total}`} 
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip 
            icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
            label={`Compliant: ${stats.compliant}`} 
            sx={{ bgcolor: 'success.50', color: 'success.dark', fontWeight: 600, border: '1px solid', borderColor: 'success.200', '& .MuiChip-icon': { color: 'success.main' } }}
          />
          {stats.expiringSoon > 0 && (
            <Chip 
              icon={<WarningIcon sx={{ fontSize: 16 }} />}
              label={`Expiring Soon: ${stats.expiringSoon}`} 
              sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontWeight: 600, border: '1px solid', borderColor: 'warning.200', '& .MuiChip-icon': { color: 'warning.main' } }}
            />
          )}
          {stats.nonCompliant > 0 && (
            <Chip 
              icon={<ErrorIcon sx={{ fontSize: 16 }} />}
              label={`Non-Compliant: ${stats.nonCompliant}`} 
              sx={{ bgcolor: 'error.50', color: 'error.dark', fontWeight: 600, border: '1px solid', borderColor: 'error.200', '& .MuiChip-icon': { color: 'error.main' } }}
            />
          )}
          {stats.unlinked > 0 && (
            <Chip 
              icon={<LinkOffIcon sx={{ fontSize: 16 }} />}
              label={`Not Linked: ${stats.unlinked}`} 
              sx={{ bgcolor: 'grey.100', color: 'grey.700', fontWeight: 600, border: '1px solid', borderColor: 'grey.300', '& .MuiChip-icon': { color: 'grey.500' } }}
            />
          )}
        </Box>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Club</InputLabel>
              <Select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                label="Club"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Clubs</MenuItem>
                {clubs.map((club) => (
                  <MenuItem key={club.id} value={club.id}>
                    {club.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Compliance</InputLabel>
              <Select
                value={selectedCompliance}
                onChange={(e) => setSelectedCompliance(e.target.value)}
                label="Compliance"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Compliant">Compliant</MenuItem>
                <MenuItem value="Expiring Soon">Expiring Soon</MenuItem>
                <MenuItem value="Non-Compliant">Non-Compliant</MenuItem>
                <MenuItem value="unlinked">Not USFS Linked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search coaches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
            <Button onClick={loadCoaches} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredCoaches.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
            <CoachIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || selectedClub ? 'No coaches found' : 'No approved coaches yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'Try adjusting your search terms.' : 'Coaches will appear here once approved.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 2,
            '& .MuiTableCell-head': {
              bgcolor: 'grey.50',
              fontWeight: 700
            }
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortField === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Coach
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'email' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'email'}
                      direction={sortField === 'email' ? sortDirection : 'asc'}
                      onClick={() => handleSort('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'club' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'club'}
                      direction={sortField === 'club' ? sortDirection : 'asc'}
                      onClick={() => handleSort('club')}
                    >
                      Club
                    </TableSortLabel>
                  </TableCell>
                  {showInterclubColumn && (
                    <TableCell sortDirection={sortField === 'interclub' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortField === 'interclub'}
                        direction={sortField === 'interclub' ? sortDirection : 'asc'}
                        onClick={() => handleSort('interclub')}
                      >
                        Interclub
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell align="center">Approval</TableCell>
                  <TableCell align="center" sortDirection={sortField === 'compliance' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'compliance'}
                      direction={sortField === 'compliance' ? sortDirection : 'asc'}
                      onClick={() => handleSort('compliance')}
                    >
                      Compliance
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'skaters' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'skaters'}
                      direction={sortField === 'skaters' ? sortDirection : 'asc'}
                      onClick={() => handleSort('skaters')}
                    >
                      Skaters
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCoaches.map((coach) => (
                  <TableRow 
                    key={coach.id} 
                    hover
                    onClick={() => handleCoachClick(coach)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ 
                          bgcolor: 'success.main', 
                          width: 36, 
                          height: 36,
                          fontSize: '0.875rem'
                        }}>
                          {(coach.displayName || 'C')[0].toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {titleCaseName(coach.displayName) || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {coach.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{coach.clubName || '—'}</TableCell>
                    {showInterclubColumn && (
                      <TableCell>{coach.interclubName || '—'}</TableCell>
                    )}
                    <TableCell align="center">
                      {coach.coachApprovalStatus === 'approved' ? (
                        <Chip label="Approved" size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                      ) : coach.coachApprovalStatus === 'pending' ? (
                        <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {coach.isUsfsLinked ? (
                        coach.complianceStatus ? (
                          <Chip
                            icon={
                              coach.complianceStatus === 'Compliant' ? <VerifiedIcon sx={{ fontSize: 16 }} /> :
                              coach.complianceStatus === 'Expiring Soon' ? <WarningIcon sx={{ fontSize: 16 }} /> :
                              coach.complianceStatus === 'Non-Compliant' ? <ErrorIcon sx={{ fontSize: 16 }} /> :
                              null
                            }
                            label={coach.complianceStatus}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              ...(coach.complianceStatus === 'Compliant' && {
                                bgcolor: 'success.50',
                                color: 'success.dark',
                                '& .MuiChip-icon': { color: 'success.main' }
                              }),
                              ...(coach.complianceStatus === 'Expiring Soon' && {
                                bgcolor: 'warning.50',
                                color: 'warning.dark',
                                '& .MuiChip-icon': { color: 'warning.main' }
                              }),
                              ...(coach.complianceStatus === 'Non-Compliant' && {
                                bgcolor: 'error.50',
                                color: 'error.dark',
                                '& .MuiChip-icon': { color: 'error.main' }
                              }),
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                            label="Linked - Refresh Needed"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: 'info.50',
                              color: 'info.dark',
                              '& .MuiChip-icon': { color: 'info.main' }
                            }}
                          />
                        )
                      ) : (
                        <Chip
                          icon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                          label="Not Linked"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: 'grey.100',
                            color: 'grey.600',
                            '& .MuiChip-icon': { color: 'grey.500' }
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={coach.skaterCount}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          minWidth: 40,
                          bgcolor: coach.skaterCount > 0 ? 'success.100' : 'grey.100',
                          color: coach.skaterCount > 0 ? 'success.dark' : 'text.secondary',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedCoaches.length} of {stats.total} coach{stats.total !== 1 ? 'es' : ''}
          </Typography>
        </Box>
      </Paper>

      {/* Coach Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedCoach && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    width: 56, 
                    height: 56, 
                    bgcolor: 'success.main',
                    fontSize: '1.5rem'
                  }}>
                    {(selectedCoach.displayName || 'C')[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {titleCaseName(selectedCoach.displayName) || 'Unknown Coach'}
                    </Typography>
                    <Chip
                      icon={<CoachIcon sx={{ fontSize: 16 }} />}
                      label="Approved Coach"
                      size="small"
                      sx={{ 
                        mt: 0.5,
                        bgcolor: 'success.50', 
                        color: 'success.dark',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: 'success.main' }
                      }}
                    />
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetail} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Contact Info */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCoach.email || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCoach.phone || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClubIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Club</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCoach.clubName || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Coach Compliance */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Coach Compliance
                  </Typography>
                </Grid>
                {selectedCoach.isUsfsLinked ? (
                  selectedCoach.coachCredentials ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {selectedCoach.complianceStatus === 'Compliant' ? (
                            <VerifiedIcon color="success" fontSize="small" />
                          ) : selectedCoach.complianceStatus === 'Expiring Soon' ? (
                            <WarningIcon color="warning" fontSize="small" />
                          ) : (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                          <Box>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: selectedCoach.complianceStatus === 'Compliant' ? 'success.main' :
                                       selectedCoach.complianceStatus === 'Expiring Soon' ? 'warning.main' : 'error.main'
                              }}
                            >
                              {selectedCoach.complianceStatus || 'Unknown'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      {selectedCoach.coachCredentials?.complianceExpires && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Expires</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {selectedCoach.coachCredentials.complianceExpires}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      {selectedCoach.coachCredentials?.highestCertification && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CoachIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Certification</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {selectedCoach.coachCredentials.highestCertification}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      {selectedCoach.coachCredentials?.displayDisciplines && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Disciplines</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {selectedCoach.coachCredentials.displayDisciplines}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      {selectedCoach.coachCredentials?.homeClub && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ClubIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">USFS Home Club</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {selectedCoach.coachCredentials.homeClub}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 2,
                        bgcolor: 'info.50',
                        borderRadius: 2
                      }}>
                        <VerifiedIcon color="info" />
                        <Typography variant="body2" color="info.dark">
                          USFS account is linked. Coach needs to click "Refresh Data" on their Profile page to sync compliance data.
                        </Typography>
                      </Box>
                    </Grid>
                  )
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2
                    }}>
                      <LinkOffIcon color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Coach has not linked their USFS account. Compliance cannot be verified.
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Coached Skaters */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Coached Skaters ({selectedCoach.skaterCount})
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={scanLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                      onClick={handleScanForSkaters}
                      disabled={scanLoading}
                      sx={{ textTransform: 'none' }}
                    >
                      {scanLoading ? 'Scanning…' : 'Scan for skaters'}
                    </Button>
                  </Box>
                </Grid>
                {scanError && (
                  <Grid item xs={12}>
                    <Alert severity="error" onClose={() => setScanError(null)} sx={{ borderRadius: 2 }}>
                      {scanError}
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  {selectedCoach.skaters && selectedCoach.skaters.length > 0 ? (
                    <List dense sx={{ 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}>
                      {selectedCoach.skaters.map((skater, index) => (
                        <ListItem 
                          key={skater.id || index}
                          sx={{
                            borderBottom: index < selectedCoach.skaters.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.75rem' }}>
                              {(skater.firstName || 'S')[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {skater.firstName} {skater.lastName}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                {skater.currentLevel && (
                                  <Chip 
                                    label={skater.currentLevel} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {skater.membershipId && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    USFS: {skater.membershipId}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 3, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2 
                    }}>
                      <SkaterIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No skaters linked to this coach yet
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseDetail} variant="outlined" sx={{ borderRadius: 2 }}>
                Close
              </Button>
              {selectedCoach.email && (
                <Button 
                  variant="contained" 
                  href={`mailto:${selectedCoach.email}`}
                  startIcon={<EmailIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Email Coach
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
